import React from "react";
import moment from "moment";
import { FormattedMessage } from "react-intl";
import { makeStyles, Typography } from "@material-ui/core";

import PageContainer from "../custom/Page";
import ErrorMessage from "./components/ErrorMessage";
import LoadingSpinner from "./components/LoadingSpinner";
import AnimatedCardContent from "./components/AnimatedCardContent";
import ContractsByAgentFilter from "./components/Reports/Contract/byAgent/ContractsByAgentFilter";
import HeaderWithHelpDialog from "./components/Reports/Contract/byAgent/HeaderWithHelpDialog";
import ContractReportTable from "./components/Reports/Contract/ContractReportTable";
import { CONTRACT_BY_AGENT_FILTER_KEYS } from "./components/Reports/Contract/byAgent/filterKeys";

import { getContractsByAgent } from "../actions";
import useFetchAgentsBelow from "../hooks/useFetchAgentsBelow";
import useGetCurrentUser from "../hooks/useGetCurrentUser";
import useFetchUsersBelowTargets from "../hooks/useFetchUserBelowTargets";
import { CONTRACT_DURATIONS, CONTRACT_SITUATIONS_DEFAULT, CONTRACT_TYPES, CUSTOMER_ROUTES, HierarchyViewTypeEnum, removeEmptyFields, SORT } from "../util";

const useStyles = makeStyles((theme) => ({
  textAlignCenter: {
    textAlign: "center",
    marginTop: theme.spacing(2),
  },
}));

const INITIAL_FILTERS = {
  [CONTRACT_BY_AGENT_FILTER_KEYS.AGENTS]: [],
  [CONTRACT_BY_AGENT_FILTER_KEYS.USERS]: [],
  [CONTRACT_BY_AGENT_FILTER_KEYS.HIERARCHY_VIEW_TYPE]: HierarchyViewTypeEnum.DIRECTLY,

  [CONTRACT_BY_AGENT_FILTER_KEYS.CONTRACT_SITUATIONS]: CONTRACT_SITUATIONS_DEFAULT,
  [CONTRACT_BY_AGENT_FILTER_KEYS.CONTRACT_MODALITIES]: Object.values(CONTRACT_TYPES),
  [CONTRACT_BY_AGENT_FILTER_KEYS.CONTRACT_DURATIONS]: CONTRACT_DURATIONS,

  [CONTRACT_BY_AGENT_FILTER_KEYS.DATE_COLUMN]: "",
  [CONTRACT_BY_AGENT_FILTER_KEYS.START_DATE]: moment().startOf("month"),
  [CONTRACT_BY_AGENT_FILTER_KEYS.END_DATE]: moment().endOf("month"),

  [CONTRACT_BY_AGENT_FILTER_KEYS.ORDER_BY]: "contractId",
  [CONTRACT_BY_AGENT_FILTER_KEYS.SORT]: SORT.DESC,
};

const RESET_USERS_ON_CHANGE = [CONTRACT_BY_AGENT_FILTER_KEYS.AGENTS, CONTRACT_BY_AGENT_FILTER_KEYS.HIERARCHY_VIEW_TYPE];

const fetchContractsReport = async ({ skip = 0, take = 40, params }) => {
  const { users, agents, dateColumn, startDate, endDate, ...filters } = params;

  const reportParams = {
    ...filters,
    skip,
    take,
    ...(dateColumn && {
      dateColumn,
      startDate: startDate.format("YYYY-MM-DD"),
      endDate: endDate.format("YYYY-MM-DD"),
    }),
    userIds: users?.map((u) => u.id) ?? [],
    agentIds: agents?.map((a) => a.id) ?? [],
  };
  const cleanedFilters = removeEmptyFields(reportParams);

  return getContractsByAgent(cleanedFilters);
};

const ReportContractsByAgent = () => {
  const classes = useStyles();
  const currentUser = useGetCurrentUser();

  const [filters, setFilters] = React.useState(INITIAL_FILTERS);

  const { agents, isLoading: loadingAgents, error: errorAgents } = useFetchAgentsBelow(currentUser);
  const { users, loading: loadingUsers, error: errorUsers } = useFetchUsersBelowTargets(filters[CONTRACT_BY_AGENT_FILTER_KEYS.AGENTS], filters[CONTRACT_BY_AGENT_FILTER_KEYS.HIERARCHY_VIEW_TYPE]);

  const hasSelectedAgents = React.useMemo(() => filters[CONTRACT_BY_AGENT_FILTER_KEYS.AGENTS]?.length > 0, [filters]);

  const updateFilter = React.useCallback((key, value) => {
    const shouldResetUsers = RESET_USERS_ON_CHANGE.includes(key);

    setFilters((prev) => ({
      ...prev,
      [key]: value,
      ...(shouldResetUsers ? { users: [] } : {}),
    }));
  }, []);

  return (
    <PageContainer full>
      <HeaderWithHelpDialog />

      <ContractsByAgentFilter
        filters={filters}
        updateFilter={updateFilter}
        currentUser={currentUser}
        agents={agents}
        users={users}
        errorAgents={errorAgents}
        errorUsers={errorUsers}
      />

      {loadingAgents ? (
        <LoadingSpinner
          marginTop={40}
          message={<FormattedMessage id="app.agents.searching" />}
        />
      ) : errorAgents ? (
        <ErrorMessage>
          <FormattedMessage
            id={errorAgents}
            defaultMessage={errorAgents}
          />
        </ErrorMessage>
      ) : !hasSelectedAgents ? (
        <AnimatedCardContent raised={false}>
          <Typography
            color="primary"
            variant="h6"
            className={classes.textAlignCenter}
          >
            <FormattedMessage id="app.report.contract.byAgent.filters" />
          </Typography>
        </AnimatedCardContent>
      ) : loadingUsers ? (
        <LoadingSpinner
          marginTop={40}
          message={<FormattedMessage id="app.loading.fetching.users" />}
        />
      ) : errorUsers ? (
        <ErrorMessage>
          <FormattedMessage
            id={errorUsers}
            defaultMessage={errorUsers}
          />
        </ErrorMessage>
      ) : (
        <ContractReportTable
          currentUser={currentUser}
          fetchContractsReport={fetchContractsReport}
          filters={filters}
          setOrderBy={(value) => updateFilter(CONTRACT_BY_AGENT_FILTER_KEYS.ORDER_BY, value)}
          setSort={(value) => updateFilter(CONTRACT_BY_AGENT_FILTER_KEYS.SORT, value)}
          loadingAgentsOrUsers={loadingUsers || loadingAgents}
          showExclusiveCommissions
          reportKey="reportContractsByAgent"
          customerRoutePrefix={CUSTOMER_ROUTES.MANAGER}
        />
      ) }
    </PageContainer>
  );
};

export default ReportContractsByAgent;
